/* JPI universal Langify and Shopify Inbox coordination. */
(() => {
  const LANGUAGE_SELECTOR = '.ly-switcher-wrapper';
  const CHAT_SELECTOR = 'shopify-chat, #ShopifyChat, shopify-agent';
  const NATIVE_SELECTOR = [
    '.jpi-universal-utilities[data-jpi-localization-mode="native"]',
    '.jpi-header__localization',
    '.jpi-header__mobile-localization'
  ].join(',');
  const LANGUAGE_PRESENTATION = {
    en: { label: 'English', lang: 'en', direction: 'ltr', script: 'latin' },
    es: { label: 'Español', lang: 'es', direction: 'ltr', script: 'latin' },
    ar: { label: 'العربية', lang: 'ar', direction: 'rtl', script: 'arabic' },
    'zh-cn': { label: '简体中文', lang: 'zh-CN', direction: 'ltr', script: 'han' },
    hi: { label: 'हिन्दी', lang: 'hi', direction: 'ltr', script: 'devanagari' },
    ne: { label: 'नेपाली', lang: 'ne', direction: 'ltr', script: 'devanagari' },
    fr: { label: 'Français', lang: 'fr', direction: 'ltr', script: 'latin' },
    gd: { label: 'Gàidhlig', lang: 'gd', direction: 'ltr', script: 'latin' },
    ga: { label: 'Gaeilge', lang: 'ga', direction: 'ltr', script: 'latin' },
    fa: { label: 'فارسی', lang: 'fa', direction: 'rtl', script: 'arabic' },
    'pt-br': { label: 'Português (Brasil)', lang: 'pt-BR', direction: 'ltr', script: 'latin' }
  };
  const chatOpenObservers = new WeakMap();
  let scanQueued = false;

  function suppressFirstOrderOffer() {
    const shouldSuppress = Boolean(document.querySelector('[data-jpi-suppress-first-order-offer]'));
    document.documentElement.toggleAttribute('data-jpi-suppress-first-order-offer', shouldSuppress);
    if (!shouldSuppress) return;

    document.querySelectorAll('#ecomsend-widget, .EcomSendAPP').forEach((element) => {
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.style.setProperty('display', 'none', 'important');
    });

    document.querySelectorAll('.react-responsive-modal-root').forEach((root) => {
      const isFirstOrderOffer = root.querySelector(
        '.react-responsive-modal-modal.pp-modal, #trustoo-popup-optIn-title, .ecomsend__Modal__Content'
      );
      if (!isFirstOrderOffer) return;
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      root.style.setProperty('display', 'none', 'important');
    });
  }

  function syncUtilityOffsets(chatControls) {
    const activatorRects = Array.from(chatControls)
      .map((control) => control.shadowRoot?.querySelector('[part="activator"], .activator'))
      .filter(Boolean)
      .map((activator) => activator.getBoundingClientRect())
      .filter((rect) => rect.width > 0 && rect.height > 0);

    if (!activatorRects.length) return;

    const highestActivatorTop = Math.min(...activatorRects.map((rect) => rect.top));
    const rootStyles = getComputedStyle(document.documentElement);
    const configuredGap = Number.parseFloat(rootStyles.getPropertyValue('--jpi-app-utility-gap')) || 16;
    const requiredBottom = Math.max(0, window.innerHeight - highestActivatorTop + configuredGap);
    document.documentElement.style.setProperty('--jpi-app-utility-language-bottom', `${Math.ceil(requiredBottom)}px`);
  }

  function syncUtilityStacking() {
    const languageControls = document.querySelectorAll(LANGUAGE_SELECTOR);
    const chatControls = document.querySelectorAll(CHAT_SELECTOR);
    const chatIsOpen = Array.from(chatControls).some((control) => control.hasAttribute('open'));

    languageControls.forEach((control) => {
      control.style.setProperty('z-index', 'var(--jpi-app-utility-z-language)', 'important');
    });

    chatControls.forEach((control) => {
      control.style.setProperty('z-index', 'var(--jpi-app-utility-z-chat)', 'important');
      control.style.setProperty('--shopify-chat-z-index', 'var(--jpi-app-utility-z-chat)');
      control.style.setProperty('--shopify-agent-z-index', 'var(--jpi-app-utility-z-chat)');
      control.style.setProperty('--shopify-chat-border-radius', '24px', 'important');
      control.style.setProperty('--shopify-agent-border-radius', '24px', 'important');

      if (chatOpenObservers.has(control)) return;
      const observer = new MutationObserver(syncUtilityStacking);
      observer.observe(control, { attributes: true, attributeFilter: ['open'] });
      chatOpenObservers.set(control, observer);
    });

    document.documentElement.toggleAttribute('data-jpi-chat-open', chatIsOpen);
    syncUtilityOffsets(chatControls);
  }

  function toLanguageAbbreviation(value) {
    return String(value || '')
      .trim()
      .split('-')[0]
      .slice(0, 2)
      .toUpperCase();
  }

  function decorateLanguageControl(control) {
    const selectedLink = control.querySelector('.ly-languages-switcher-link[aria-current="true"]');
    const selectedCode = selectedLink?.dataset.languageCode || document.documentElement.lang || 'en';
    const selectedAbbreviation = toLanguageAbbreviation(selectedCode);
    const selectedPresentation = LANGUAGE_PRESENTATION[String(selectedCode).toLowerCase()] || LANGUAGE_PRESENTATION.en;

    control.querySelectorAll('.ly-custom-dropdown-current, .ly-popup-current').forEach((current) => {
      current.dataset.jpiLanguageAbbreviation = selectedAbbreviation;
      current.setAttribute('aria-label', `Selected language: ${selectedPresentation.label}`);
      const currentLabel = current.querySelector('.ly-custom-dropdown-current-inner-text');
      if (currentLabel) {
        currentLabel.textContent = selectedPresentation.label;
        currentLabel.lang = selectedPresentation.lang;
        currentLabel.dir = selectedPresentation.direction;
        currentLabel.dataset.jpiLanguageScript = selectedPresentation.script;
      }
    });

    control.querySelectorAll('.ly-languages-switcher-link[data-language-code]').forEach((link) => {
      const languageCode = link.dataset.languageCode;
      const presentation = LANGUAGE_PRESENTATION[String(languageCode).toLowerCase()];
      link.dataset.jpiLanguageAbbreviation = toLanguageAbbreviation(languageCode);
      if (!presentation) return;

      link.lang = presentation.lang;
      link.dir = 'ltr';
      const label = link.querySelector('.ly-custom-dropdown-list-element-right, .ly-inner-text');
      if (label) {
        label.textContent = presentation.label;
        label.lang = presentation.lang;
        label.dir = presentation.direction;
        label.dataset.jpiLanguageScript = presentation.script;
      }
    });
  }

  function reconcileNativeControls(hasLangify) {
    document.querySelectorAll(NATIVE_SELECTOR).forEach((control) => {
      if (hasLangify) {
        if (!control.hidden) control.dataset.jpiSuppressedByLangify = 'true';
        control.hidden = true;
      } else if (control.dataset.jpiSuppressedByLangify === 'true') {
        control.hidden = false;
        delete control.dataset.jpiSuppressedByLangify;
      }
    });
  }

  function scan() {
    scanQueued = false;
    suppressFirstOrderOffer();
    const languageControls = document.querySelectorAll(LANGUAGE_SELECTOR);
    const chatControls = document.querySelectorAll(CHAT_SELECTOR);

    languageControls.forEach((control) => {
      control.dataset.jpiUniversalAppUtility = 'language';
      control.classList.add('jpi-app-utility--language');
      decorateLanguageControl(control);
    });

    chatControls.forEach((control) => {
      control.dataset.jpiUniversalAppUtility = 'chat';
      control.classList.add('jpi-app-utility--chat');
    });

    document.documentElement.toggleAttribute('data-jpi-has-langify', languageControls.length > 0);
    document.documentElement.toggleAttribute('data-jpi-has-shopify-chat', chatControls.length > 0);
    reconcileNativeControls(languageControls.length > 0);
    syncUtilityStacking();
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(scan);
  }

  function start() {
    /* TIMESTAMP START 2026-09-04T15:08:06-04:00 | REVISION R000882
     * Keep the universal utility scan in its original scope; immediate launcher
     * creation belongs to the compact utility controller below.
     */
    scan();
    /* TIMESTAMP END 2026-09-04T15:08:06-04:00 | REVISION R000882 */
    if (!document.documentElement) return;

    const observer = new MutationObserver(scheduleScan);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);

    window.addEventListener('load', scan, { once: true });
    window.addEventListener('resize', scheduleScan, { passive: true });
    document.addEventListener('shopify:section:load', scheduleScan);
    document.addEventListener('shopify:block:select', scheduleScan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* TIMESTAMP START 2026-08-18T16:01:31-04:00 | REVISION R000008 */
/* Collapse the multi-app utility rail at narrow or short viewports. */
(() => {
  /* TIMESTAMP START 2026-09-04T15:08:06-04:00 | REVISION R000882
 * Keep the utility hub available at every viewport size so the CSS-controlled
 * three-dot launcher is never hidden on wide storefront templates.
 */
const COMPACT_UTILITY_MEDIA = window.matchMedia('all');
/* TIMESTAMP END 2026-09-04T15:08:06-04:00 | REVISION R000882 */
  const UTILITY_SELECTOR = [
    '.ly-switcher-wrapper',
    'shopify-chat',
    '#ShopifyChat',
    'shopify-agent',
    '#Sea-Accessibility__ButtonWidget',
    '.nm-wishlist-open-list-button'
  ].join(',');
  const CHAT_SELECTOR = 'shopify-chat, #ShopifyChat, shopify-agent';
  const COLLAPSE_AFTER_ACTIVATION_SELECTOR =
    '#Sea-Accessibility__ButtonWidget, .nm-wishlist-open-list-button';
  const TOGGLE_ID = 'jpi-utility-toggle';
  const collapseBoundControls = new WeakSet();
  let expanded = false;
  let scanQueued = false;

  function ensureToggle() {
    let toggle = document.getElementById(TOGGLE_ID);
    if (toggle) return toggle;

    toggle = document.createElement('button');
    toggle.id = TOGGLE_ID;
    toggle.className = 'jpi-utility-toggle';
    toggle.type = 'button';
    toggle.innerHTML = '<span class="jpi-utility-toggle__icon" aria-hidden="true"></span>';
    toggle.addEventListener('click', () => setExpanded(!expanded));
    document.body.append(toggle);
    return toggle;
  }

  function setExpanded(nextExpanded) {
    expanded = Boolean(nextExpanded) && COMPACT_UTILITY_MEDIA.matches;
    sync();
  }


  function getProtectedRects() {
    const rects = [];

    document.querySelectorAll(
      '#MainContent input, #MainContent textarea, #MainContent select, ' +
      '#MainContent button, #MainContent [role="button"], #MainContent a.button, ' +
      '#MainContent #dynamic-checkout-cart, #MainContent shopify-accelerated-checkout, ' +
      '#MainContent shopify-accelerated-checkout-cart'
    ).forEach((element) => {
      const styles = getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      if (
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight
      ) {
        rects.push(rect);
      }
    });

    document.querySelectorAll(
      '#MainContent h1, #MainContent h2, #MainContent h3, ' +
      '#MainContent p, #MainContent label, #MainContent .price'
    ).forEach((element) => {
      if (!element.textContent.trim()) return;
      const range = document.createRange();
      range.selectNodeContents(element);
      Array.from(range.getClientRects()).forEach((rect) => {
        if (rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight) {
          rects.push(rect);
        }
      });
    });

    return rects;
  }

  function rectanglesIntersect(first, second) {
    return (
      first.right > second.left &&
      first.left < second.right &&
      first.bottom > second.top &&
      first.top < second.bottom
    );
  }

  function positionToggle(toggle, isOpen) {
    if (!COMPACT_UTILITY_MEDIA.matches || isOpen || toggle.hidden) return;

    const root = document.documentElement;
    root.style.removeProperty('--jpi-app-utility-toggle-bottom');

    const baseRect = toggle.getBoundingClientRect();
    const baseBottom = Math.max(0, window.innerHeight - baseRect.bottom);
    const protectedRects = getProtectedRects();
    const maximumSteps = Math.max(0, Math.floor((window.innerHeight - 80) / 60));

    for (let step = 0; step <= maximumSteps; step += 1) {
      const candidateBottom = baseBottom + step * 60;
      const candidateTop = window.innerHeight - candidateBottom - baseRect.height;
      if (candidateTop < 8) break;

      const candidateRect = {
        bottom: candidateTop + baseRect.height,
        left: baseRect.left,
        right: baseRect.right,
        top: candidateTop
      };

      if (!protectedRects.some((rect) => rectanglesIntersect(candidateRect, rect))) {
        root.style.setProperty(
          '--jpi-app-utility-toggle-bottom',
          Math.round(candidateBottom) + 'px'
        );
        return;
      }
    }

    root.style.setProperty(
      '--jpi-app-utility-toggle-bottom',
      Math.round(baseBottom) + 'px'
    );
  }

  function sync() {
    const utilityControls = document.querySelectorAll(UTILITY_SELECTOR);
    const toggle = document.getElementById(TOGGLE_ID);

    if (!utilityControls.length) {
      toggle?.remove();
      document.documentElement.removeAttribute('data-jpi-utilities-open');
      return;
    }

    const isCompact = COMPACT_UTILITY_MEDIA.matches;
    const chatIsOpen = Array.from(document.querySelectorAll(CHAT_SELECTOR)).some((control) =>
      control.hasAttribute('open')
    );

    if (!isCompact || chatIsOpen) expanded = false;

    const isOpen = isCompact && expanded;
    const utilityToggle = ensureToggle();
    utilityToggle.hidden = !isCompact || chatIsOpen;
    utilityToggle.setAttribute('aria-expanded', String(isOpen));
    utilityToggle.setAttribute(
      'aria-label',
      isOpen
        ? 'Close language, accessibility, and chat tools'
        : 'Open language, accessibility, and chat tools'
    );
    document.documentElement.toggleAttribute('data-jpi-utilities-open', isOpen);
    positionToggle(utilityToggle, isOpen);

    document.querySelectorAll(COLLAPSE_AFTER_ACTIVATION_SELECTOR).forEach((control) => {
      if (collapseBoundControls.has(control)) return;
      control.addEventListener('click', () => window.setTimeout(() => setExpanded(false), 0));
      collapseBoundControls.add(control);
    });
  }

  function scheduleSync() {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(() => {
      scanQueued = false;
      sync();
    });
  }

  function start() {
    /* TIMESTAMP START 2026-09-04T15:08:06-04:00 | REVISION R000882
     * Create the hub immediately inside the compact controller so slow third-party
     * widgets cannot prevent the launcher from appearing.
     */
    ensureToggle();
    /* TIMESTAMP END 2026-09-04T15:08:06-04:00 | REVISION R000882 */
    sync();

    const observer = new MutationObserver(scheduleSync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['open'],
      childList: true,
      subtree: true
    });

    COMPACT_UTILITY_MEDIA.addEventListener('change', scheduleSync);
    window.addEventListener('pageshow', scheduleSync);
    window.addEventListener('resize', scheduleSync, { passive: true });
    window.addEventListener('scroll', scheduleSync, { passive: true });
    document.addEventListener('shopify:section:load', scheduleSync);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && expanded) setExpanded(false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
/* TIMESTAMP END 2026-08-18T16:01:31-04:00 | REVISION R000008 */
